import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Navigate } from 'react-router';

import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { League } from '@sorare/core/src/components/landing/types';
import { ANY_SPORT_PLAY } from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';

import { WalletLanding } from '#src/components/landing/WalletLanding/index.tsx';
import { winAssets } from '#src/pages/assets/assets.ts';

import heroBgAvif from './assets/hero/bg.avif';
import heroBg from './assets/hero/bg.webp';
import heroPlayersLeftSideAvif from './assets/hero/players/leftSidePlayers.avif';
import heroPlayersLeftSide from './assets/hero/players/leftSidePlayers.webp';
import heroPlayersRightSideAvif from './assets/hero/players/rightSidePlayers.avif';
import heroPlayersRightSide from './assets/hero/players/rightSidePlayers.webp';
import leagueLogo from './assets/league-logo.webp';
import playFieldAvif from './assets/play/field.avif';
import playField from './assets/play/field.webp';
import lineupAvif from './assets/play/lineup.avif';
import lineup from './assets/play/lineup.webp';
import scoutBgAvif from './assets/scout/background.avif';
import scoutBg from './assets/scout/background.webp';
import scoutPlayersAvif from './assets/scout/players.avif';
import scoutPlayers from './assets/scout/players.webp';
import social from './assets/social.webp';

const messages = defineMessages({
  metadatasTitle: {
    defaultMessage: 'The Real Fantasy Laliga Game – Sorare Football',
  },
  metadatasDescription: {
    defaultMessage:
      "Collect, play and win officially licensed digital cards featuring players from LALIGA, the world's best football league on Sorare",
  },
});

const heroAssets = {
  background: {
    avif: heroBgAvif,
    pictureUrl: heroBg,
  },
  players: [
    {
      avif: heroPlayersLeftSideAvif,
      pictureUrl: heroPlayersLeftSide,
      height: 477,
      width: 670,
    },
    {
      avif: heroPlayersRightSideAvif,
      pictureUrl: heroPlayersRightSide,
      height: 488,
      width: 657,
    },
  ],
};

const scoutAssets = {
  background: {
    avif: scoutBgAvif,
    pictureUrl: scoutBg,
  },
  players: {
    avif: scoutPlayersAvif,
    pictureUrl: scoutPlayers,
  },
};

const playAssets = {
  background: {
    avif: playFieldAvif,
    pictureUrl: playField,
  },
  lineup: {
    avif: lineupAvif,
    pictureUrl: lineup,
  },
};

const FootballPage = () => {
  const isLoggedIn = useIsLoggedIn();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const { generateSportContextPath } = useSportContext();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: social,
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );

  if (isLoggedIn) {
    return <Navigate to={generateSportContextPath(ANY_SPORT_PLAY)} replace />;
  }

  return (
    <WalletLanding
      assets={{
        heroAssets,
        scoutAssets,
        playAssets,
        winAssets,
      }}
      leaderboards={[
        So5LeaderboardType.IN_SEASON_SPAIN_LIMITED,
        So5LeaderboardType.IN_SEASON_SPAIN_UNIQUE,
        So5LeaderboardType.IN_SEASON_SPAIN_RARE,
      ]}
      leagueLogo={<Img src={leagueLogo} alt="LALIGA" width="240" height="80" />}
      league={League.LaLiga}
    />
  );
};

export default FootballPage;
